import { useId } from 'react';
import { cn } from '@/shared/lib/cn';

interface SparklineProps {
  data: number[];
  className?: string;
  /** Tailwind text colour class driving stroke/fill via currentColor. */
  tone?: 'primary' | 'success' | 'danger' | 'muted';
  area?: boolean;
}

const TONE = {
  primary: 'text-primary',
  success: 'text-success',
  danger: 'text-danger',
  muted: 'text-muted-foreground',
} as const;

/** Lightweight SVG sparkline for KPI tiles (no chart lib needed). */
export function Sparkline({ data, className, tone = 'primary', area = true }: SparklineProps) {
  const gradientId = useId();
  if (data.length < 2) return null;
  const w = 100;
  const h = 32;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const points = data.map((v, i) => [(i / (data.length - 1)) * w, h - 2 - ((v - min) / span) * (h - 4)] as const);
  const line = points.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  const fill = `${line} L${w},${h} L0,${h} Z`;
  const last = points[points.length - 1]!;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className={cn('h-full w-full overflow-visible', TONE[tone], className)} aria-hidden>
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      {area && <path d={fill} fill={`url(#${gradientId})`} />}
      <path d={line} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="2" fill="currentColor" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
